import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface AccordionProps {
  title: string;
  children: React.ReactNode;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  icon?: React.ReactNode;
  className?: string;
  /** Default open state (only used if open prop is not provided) */
  defaultOpen?: boolean;
}

/**
 * Accordion component with smooth height animation
 * Features:
 * - Smooth height transition
 * - Rotating chevron indicator
 * - Keyboard accessible (Space/Enter)
 * - Controlled or uncontrolled mode
 * - ARIA attributes for accessibility
 */
export const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  open: controlledOpen,
  onToggle,
  icon,
  className,
  defaultOpen = false,
}) => {
  // Support both controlled and uncontrolled modes
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  // Calculate content height when open state changes
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, children]);

  // Recalculate height on window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const handleToggle = () => {
    const newOpenState = !isOpen;

    if (isControlled) {
      onToggle?.(newOpenState);
    } else {
      setUncontrolledOpen(newOpenState);
      onToggle?.(newOpenState);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  const accordionId = useRef(`accordion-${Math.random().toString(36).substr(2, 9)}`);
  const triggerId = `${accordionId.current}-trigger`;
  const contentId = `${accordionId.current}-content`;

  return (
    <div className={cn('border border-gray-800 rounded-lg overflow-hidden', className)}>
      {/* Accordion Header */}
      <button
        id={triggerId}
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full flex items-center justify-between gap-3',
          'px-4 py-3 bg-gray-900 hover:bg-gray-800 active:bg-gray-700',
          'transition-colors duration-150',
          'text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset'
        )}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon && <div className="flex-shrink-0 text-gray-400">{icon}</div>}
          <span className="text-sm font-medium text-gray-200 truncate">{title}</span>
        </div>

        {/* Chevron icon */}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 flex-shrink-0',
            'transform transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Accordion Content */}
      <div
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        className={cn('overflow-hidden transition-all duration-300 ease-in-out')}
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} className="bg-gray-950">
          {children}
        </div>
      </div>
    </div>
  );
};
